import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	Input,
	Page,
	Select,
	Table,
	type TableColumn,
	useToast,
} from "@theg1group/ui";
import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PartnerForm } from "../components/PartnerForm.tsx";
import { PartnerStatusBadge } from "../components/PartnerStatusBadge.tsx";
import { RiskBadge } from "../components/RiskBadge.tsx";
import { ApiError, api } from "../lib/api.ts";
import { fmtAgo, fmtDate, fmtNum } from "../lib/format.ts";
import type { PartnerRow, Rows, SyncResult } from "../lib/types.ts";

/** Human "why is this risky" hint from riskDetail. */
function riskWhy(r: PartnerRow): string | null {
	const d = r.riskDetail;
	if (!d) return null;
	const parts: string[] = [];
	if (d.fraudLinks) parts.push(`${d.fraudLinks} fraud link${d.fraudLinks === 1 ? "" : "s"}`);
	if (d.sanctioned) parts.push("sanctioned proximity");
	if (d.topKind) parts.push(`top: ${d.topKind.replaceAll("_", " ")}`);
	return parts.length ? parts.join(" · ") : null;
}

const STATUS_FILTER = [
	{ value: "", label: "All statuses" },
	{ value: "active", label: "Active" },
	{ value: "paused", label: "Paused" },
	{ value: "terminated", label: "Terminated" },
	{ value: "terminated_fraud", label: "Terminated (fraud)" },
];

export function Partners() {
	const navigate = useNavigate();
	const qc = useQueryClient();
	const toast = useToast();
	const [q, setQ] = useState("");
	const [status, setStatus] = useState("");
	const [showNew, setShowNew] = useState(false);

	const sync = useMutation({
		mutationFn: () => api.post<SyncResult>("/api/partners/sync"),
		onSuccess: (r) => {
			toast.addToast("success", `Synced: ${r.created} new, ${r.updated} updated`);
			qc.invalidateQueries({ queryKey: ["partners"] });
		},
		onError: (e) => {
			if (e instanceof ApiError && e.status === 503) {
				toast.addToast("error", "Affiliates sync is not configured yet (OIDC secret missing).");
			} else {
				toast.addToast("error", String(e));
			}
		},
	});

	const list = useQuery({
		queryKey: ["partners", q, status],
		queryFn: () =>
			api.get<Rows<PartnerRow>>(
				`/api/partners?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}`,
			),
	});

	// Default sort: highest risk first (triage view).
	const rows = useMemo(
		() => [...(list.data?.rows ?? [])].sort((a, b) => (b.riskScore ?? 0) - (a.riskScore ?? 0)),
		[list.data],
	);

	const columns: TableColumn<PartnerRow>[] = [
		{
			id: "name",
			header: "Partner",
			accessor: (r) => (
				<div>
					<Link to={`/partners/${r.id}`} className="text-primary hover:underline font-medium">
						{r.name}
					</Link>
					{r.externalRef && <div className="text-[11px] text-muted">{r.externalRef}</div>}
				</div>
			),
		},
		{ id: "status", header: "Status", accessor: (r) => <PartnerStatusBadge status={r.status} /> },
		{
			id: "risk",
			header: "Risk",
			accessor: (r) => {
				const why = riskWhy(r);
				return (
					<span className="inline-flex items-center gap-1">
						<RiskBadge score={r.riskScore ?? 0} />
						{why && (
							<span title={why} className="text-danger inline-flex">
								<AlertTriangle size={13} />
							</span>
						)}
					</span>
				);
			},
		},
		{
			id: "source",
			header: "Source",
			accessor: (r) => (
				<span className="inline-flex items-center gap-1">
					{r.source === "affiliates" ? (
						<Badge variant="info">Affiliates</Badge>
					) : (
						<Badge>Local</Badge>
					)}
					{r.isInternal && (
						<Badge size="sm" className="text-muted">
							internal
						</Badge>
					)}
				</span>
			),
		},
		{ id: "wallets", header: "Wallets", accessor: (r) => fmtNum(r.walletCount) },
		{
			id: "alerts",
			header: "Open alerts",
			accessor: (r) =>
				r.openAlerts ? (
					<span className="text-danger font-semibold">{r.openAlerts}</span>
				) : (
					<span className="text-muted">0</span>
				),
		},
		{
			id: "link",
			header: "Link max",
			accessor: (r) => {
				if (r.linkMax == null) return "—";
				const v = Number(r.linkMax);
				return <span className={v >= 60 ? "text-danger font-semibold" : ""}>{v.toFixed(2)}</span>;
			},
		},
		{
			id: "activity",
			header: "Last activity",
			accessor: (r) => <span title={fmtDate(r.lastActivity)}>{fmtAgo(r.lastActivity)}</span>,
		},
		{ id: "created", header: "Created", accessor: (r) => fmtDate(r.createdAt, false) },
	];

	return (
		<Page
			title="Partners"
			description="Affiliates / partners and their declared wallets."
			actions={
				<div className="flex gap-2">
					<Button variant="secondary" loading={sync.isPending} onClick={() => sync.mutate()}>
						Sync from Affiliates
					</Button>
					<Button variant="primary" onClick={() => setShowNew(true)}>
						New partner
					</Button>
				</div>
			}
		>
			<div className="mb-3 flex flex-wrap gap-2">
				<div className="w-72">
					<Input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Search name / external ref…"
					/>
				</div>
				<div className="w-52">
					<Select
						value={status}
						onChange={(e) => setStatus(e.target.value)}
						options={STATUS_FILTER}
					/>
				</div>
			</div>
			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={columns}
						data={rows}
						keyExtractor={(r) => r.id}
						loading={list.isLoading}
						emptyMessage="No partners yet — create one."
					/>
				</div>
			</Card>
			<PartnerForm
				open={showNew}
				onClose={() => setShowNew(false)}
				onSaved={(p) => navigate(`/partners/${p.id}`)}
			/>
		</Page>
	);
}
